import React, { useState, useMemo, useEffect } from 'react';
import AnalysisPromptCard from '../../shared/AnalysisPromptCard';
import GravityChatPanel from '../../gravityChat/GravityChatPanel';
import GravityChatCta from './GravityChatCta';
import { PlanetIcon, SignIcon } from '../../shared/AstroIcon';
import AspectMiniChart, { aspectKindFor } from '../../shared/AspectMiniChart';
import './PlanetsTab.css';

const excludedPlanets = ['South Node', 'Part of Fortune'];

// Canonical order for planet tabs
// Ascendant first, then Sun/Moon, then the planets by distance from
// the Sun, then the remaining points
const planetOrder = [
  'Ascendant', 'Sun', 'Moon', 'Mercury', 'Venus', 'Mars',
  'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto',
  'Midheaven', 'Node', 'North Node', 'Chiron'
];

// Helper function to split interpretation text into paragraphs
const splitIntoParagraphs = (text) => {
  if (!text) return null;
  return text.split('\n\n').map((para, idx) => (
    <p key={idx}>{para}</p>
  ));
};

function PlanetsTab({ birthChart, basicAnalysis, hasAnalysis, onNavigateToAnalysis, creditCost, creditsRemaining, chartId, isCelebrity = false, canUseGravityChat = false, onEmphasizeBodies, onHoverBodies, onUserSelectPlanet, externalPlanet, showAspectTable = true }) {
  const planets = useMemo(() => {
    const rawPlanets = birthChart?.planets?.filter(p => !excludedPlanets.includes(p.name)) || [];
    // Sort planets by the canonical order
    return rawPlanets.sort((a, b) => {
      const indexA = planetOrder.indexOf(a.name);
      const indexB = planetOrder.indexOf(b.name);
      // If not in order list, put at end
      const orderA = indexA === -1 ? 999 : indexA;
      const orderB = indexB === -1 ? 999 : indexB;
      return orderA - orderB;
    });
  }, [birthChart?.planets]);

  const [selectedPlanet, setSelectedPlanet] = useState(planets[0]?.name || null);
  const [chatOpen, setChatOpen] = useState(false);
  const [openAspectIdx, setOpenAspectIdx] = useState(null);

  const houses = useMemo(() => birthChart?.houses || [], [birthChart?.houses]);
  const ascendantDegree = useMemo(() => {
    const h1 = houses.find((h) => h.house === 1);
    return typeof h1?.degree === 'number' ? h1.degree : 0;
  }, [houses]);

  // the host can drive selection (e.g. clicking a planet in the 3D sky)
  useEffect(() => {
    if (externalPlanet?.name && planets.find((p) => p.name === externalPlanet.name)) {
      setSelectedPlanet(externalPlanet.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPlanet]);

  // Update selectedPlanet when planets load or change
  useEffect(() => {
    if (planets.length > 0 && (!selectedPlanet || !planets.find(p => p.name === selectedPlanet))) {
      setSelectedPlanet(planets[0].name);
    }
  }, [planets, selectedPlanet]);

  // Close any open mini chart when switching planets
  useEffect(() => {
    setOpenAspectIdx(null);
  }, [selectedPlanet]);

  // Reader-margin emphasis: the picked planet stays lit in the 3D sky.
  // No-op in the classic tabbed page (props absent).
  useEffect(() => {
    onEmphasizeBodies?.(selectedPlanet ? [selectedPlanet] : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanet]);

  const getPlanetInterpretation = (planetName) => {
    if (!basicAnalysis?.planets) return null;
    return basicAnalysis.planets[planetName];
  };

  const getPlanetByName = (planetName) => {
    return planets.find(p => p.name === planetName);
  };

  const HOUSE_BY_ANGLE = {
    Ascendant: 1,
    Descendant: 7,
    Midheaven: 10,
    MC: 10,
    IC: 4,
  };

  const resolvePosition = (planetName) => {
    const houseNum = HOUSE_BY_ANGLE[planetName];
    if (houseNum) {
      const h = houses.find((x) => x.house === houseNum);
      if (!h) return null;
      // Angles are house cusps themselves — omit `house` to avoid the
      // redundant "Ascendant · 1st house" label.
      return {
        sign: h.sign,
        degreeInSign: typeof h.degree === 'number' ? h.degree % 30 : undefined,
      };
    }
    const p = getPlanetByName(planetName);
    if (!p) return null;
    return {
      sign: p.sign,
      degreeInSign: typeof p.norm_degree === 'number' ? p.norm_degree : undefined,
      house: typeof p.house === 'number' ? p.house : undefined,
    };
  };

  const formatAspectName = (aspectType) => {
    if (!aspectType) return 'Aspect';
    const normalized = aspectType.toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getAspectColorClass = (aspectType) => {
    if (!aspectType) return '';
    switch (aspectType.toLowerCase()) {
      case 'conjunction': return 'planet-aspect-conjunction';
      case 'trine':
      case 'sextile': return 'planet-aspect-harmonious';
      case 'opposition':
      case 'square': return 'planet-aspect-challenging';
      default: return '';
    }
  };

  const getOrbLabel = (orb) => {
    if (orb === null || orb === undefined || Number.isNaN(orb)) return null;
    if (orb <= 0.5) return 'exact';
    if (orb <= 2) return 'close';
    return null;
  };

  // Get the selected planet data
  const currentPlanet = planets.find(p => p.name === selectedPlanet);
  const interpretation = currentPlanet ? getPlanetInterpretation(currentPlanet.name) : null;
  const currentPlanetAspects = useMemo(() => {
    if (!currentPlanet) return [];
    const allAspects = birthChart?.aspects || [];
    return allAspects
      .filter(aspect => (
        aspect.aspectedPlanet === currentPlanet.name ||
        aspect.aspectingPlanet === currentPlanet.name
      ))
      .map(aspect => {
        const otherPlanet = aspect.aspectedPlanet === currentPlanet.name
          ? aspect.aspectingPlanet
          : aspect.aspectedPlanet;
        return {
          ...aspect,
          otherPlanet,
          orbValue: typeof aspect.orb === 'number' ? aspect.orb : Number(aspect.orb)
        };
      })
      .sort((a, b) => (a.orbValue ?? 999) - (b.orbValue ?? 999));
  }, [birthChart?.aspects, currentPlanet]);

  if (planets.length === 0) {
    return (
      <div className="chart-tab-content planets-tab">
        <div className="planets-main-container">
          <div className="planets-header">
            <h3 className="planets-header-title">Planets</h3>
            {!isCelebrity && (
              <GravityChatCta
                hasFullAccess={canUseGravityChat}
                onActivate={() => setChatOpen(prev => !prev)}
              />
            )}
          </div>
          <div className="planets-empty-section">
            <p>No planetary data available yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-tab-content planets-tab">
      <div className="planets-main-container">
        {/* Header with title and gradient icon */}
        <div className="planets-header">
          <h3 className="planets-header-title">Planets</h3>
          {!isCelebrity && (
            <GravityChatCta
              hasFullAccess={canUseGravityChat}
              onActivate={() => setChatOpen(prev => !prev)}
            />
          )}
        </div>

        {/* Horizontal Tab Bar */}
        <div className="planets-tabs">
          {planets.map(planet => (
            <button
              key={planet.name}
              className={`planets-tab-btn ${selectedPlanet === planet.name ? 'planets-tab-btn--active' : ''}`}
              onClick={() => {
                setSelectedPlanet(planet.name);
                // deliberate pick (vs. the mount-time default) — hosts
                // may mirror it into the 3D sky
                onUserSelectPlanet?.(planet.name);
              }}
              // shared journey grammar: hover previews in the sky,
              // click pins (no-ops on the classic page)
              onMouseEnter={() => onHoverBodies?.([planet.name])}
              onMouseLeave={() => onHoverBodies?.(null)}
            >
              {planet.name}
            </button>
          ))}
        </div>

        {/* Planet Content */}
        {currentPlanet && (
          <div className="planets-content" key={currentPlanet.name}>
            {/* Info Row: Icon + Sign + House */}
            <div className="planet-info-row">
              <div className="planet-info-left">
                <span className="planet-main-symbol">
                  <PlanetIcon name={currentPlanet.name} size={24} />
                </span>
                <div className="planet-sign-display">
                  <span className="planet-sign-name">{currentPlanet.sign}</span>
                  <span className="planet-sign-symbol">
                    <SignIcon name={currentPlanet.sign} size={20} />
                  </span>
                </div>
                {currentPlanet.norm_degree !== undefined && (
                  <span className="planet-degree-display">
                    {currentPlanet.norm_degree.toFixed(1)}°
                  </span>
                )}
                {currentPlanet.is_retro === 'true' && (
                  <span className="planet-retro-badge">℞</span>
                )}
              </div>
              {currentPlanet.house && (
                <span className="planet-house-display">
                  {currentPlanet.house}{getOrdinalSuffix(currentPlanet.house)} House
                </span>
              )}
            </div>

            {/* Structured Position + Aspects (mobile-style) */}
            {showAspectTable && <div className="planet-details-card">
              <div className="planet-details-header">
                <div className="planet-details-title">{currentPlanet.name}</div>
                <div className="planet-details-subtitle">
                  {currentPlanet.sign}
                  {currentPlanet.house ? ` in House ${currentPlanet.house}` : ''}
                </div>
              </div>
              <div className="planet-details-section">
                <div className="planet-details-section-title">Aspects</div>
                {currentPlanetAspects.length > 0 ? (
                  <div className="planet-aspects-table">
                    <div className="planet-aspects-header">
                      <span className="planet-aspects-label">Planet</span>
                      <span className="planet-aspects-label planet-aspects-label--center">Aspect</span>
                      <span className="planet-aspects-label">Planet</span>
                      <span className="planet-aspects-label planet-aspects-label--right">Orb</span>
                    </div>
                    <div className="planet-aspects-list">
                      {currentPlanetAspects.map((aspect, idx) => {
                        const orbLabel = getOrbLabel(aspect.orbValue);
                        const otherPlanetData = getPlanetByName(aspect.otherPlanet);
                        const otherName = otherPlanetData?.name || aspect.otherPlanet || 'Unknown';
                        const orbValue = typeof aspect.orbValue === 'number' ? aspect.orbValue.toFixed(2) : null;
                        const selfPos = resolvePosition(currentPlanet.name);
                        const otherPos = resolvePosition(otherName);
                        const canRender = Boolean(selfPos?.sign && otherPos?.sign);
                        const isOpen = openAspectIdx === idx;
                        const rowKey = `${aspect.aspectType}-${otherName}-${idx}`;
                        const toggleAspect = () => {
                          if (!canRender) return;
                          const nextOpen = !isOpen;
                          setOpenAspectIdx(nextOpen ? idx : null);
                          onEmphasizeBodies?.(
                            nextOpen
                              ? [currentPlanet.name, otherName]
                              : [currentPlanet.name]
                          );
                        };
                        return (
                          <React.Fragment key={rowKey}>
                            <div
                              className={`planet-aspect-row ${getAspectColorClass(aspect.aspectType)}${isOpen ? ' planet-aspect-row--open' : ''}`}
                              onMouseEnter={() => onHoverBodies?.([currentPlanet.name, otherName])}
                              onMouseLeave={() => onHoverBodies?.(null)}
                              onClick={toggleAspect}
                              role={canRender ? 'button' : undefined}
                              tabIndex={canRender ? 0 : undefined}
                              aria-expanded={canRender ? isOpen : undefined}
                              onKeyDown={(e) => {
                                if (!canRender) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleAspect();
                                }
                              }}
                              style={canRender ? { cursor: 'pointer' } : undefined}
                            >
                              <div className="planet-aspect-planet">
                                <span className="planet-symbol">
                                  <PlanetIcon name={currentPlanet.name} size={16} />
                                </span>
                                <span className="planet-name">{currentPlanet.name}</span>
                              </div>
                              <div className="planet-aspect-type">
                                {orbLabel ? `${orbLabel} ` : ''}
                                {formatAspectName(aspect.aspectType)}
                              </div>
                              <div className="planet-aspect-planet">
                                <span className="planet-symbol">
                                  <PlanetIcon name={otherName} size={16} />
                                </span>
                                <span className="planet-name">{otherName}</span>
                              </div>
                              <div className="planet-aspect-orb">
                                {orbValue ? `${orbValue} Deg` : '—'}
                                {canRender ? <span className="planet-aspect-chev">{isOpen ? '▴' : '▾'}</span> : null}
                              </div>
                            </div>
                            {isOpen && canRender ? (
                              <div className="planet-aspect-mini">
                                <AspectMiniChart
                                  from={{
                                    name: currentPlanet.name,
                                    sign: selfPos.sign,
                                    degree: selfPos.degreeInSign,
                                    house: selfPos.house,
                                    color: currentPlanet.name === 'Sun' ? 'gold' : 'lilac',
                                  }}
                                  to={{
                                    name: otherName,
                                    sign: otherPos.sign,
                                    degree: otherPos.degreeInSign,
                                    house: otherPos.house,
                                    color: otherName === 'Sun' ? 'gold' : 'lilac',
                                  }}
                                  relation={formatAspectName(aspect.aspectType)}
                                  kind={aspectKindFor(aspect.aspectType, currentPlanet.name, otherName)}
                                  orb={typeof aspect.orbValue === 'number' ? aspect.orbValue : undefined}
                                  ascendantDegree={ascendantDegree}
                                />
                              </div>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="planet-details-empty">No major aspects found</div>
                )}
              </div>
            </div>}

            {/* Interpretation paragraphs */}
            {interpretation?.interpretation ? (
              <div className="planet-interpretation-section">
                {splitIntoParagraphs(interpretation.interpretation)}
              </div>
            ) : !hasAnalysis ? (
              <AnalysisPromptCard
                message={`Get a detailed interpretation of your ${currentPlanet.name} in ${currentPlanet.sign}${currentPlanet.house ? ` in the ${currentPlanet.house}${getOrdinalSuffix(currentPlanet.house)} House` : ''}.`}
                onNavigate={onNavigateToAnalysis}
                creditCost={creditCost}
                creditsRemaining={creditsRemaining}
              />
            ) : null}
          </div>
        )}
      </div>
      {!isCelebrity && canUseGravityChat && (
        <GravityChatPanel
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          contentType="birthchart"
          contentId={chartId}
          birthChart={birthChart}
          contextLabel="About your birth chart"
          placeholderText="Ask about your birth chart..."
          suggestedQuestions={[
            "What are my greatest strengths?",
            "How does my Moon sign affect my emotions?",
            "What should I focus on for personal growth?"
          ]}
        />
      )}
    </div>
  );
}

// Helper to get ordinal suffix (1st, 2nd, 3rd, etc.)
function getOrdinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export default PlanetsTab;
